import forEach from "lodash/forEach";
import { validityOptions } from "./config";

/**
 * Returns the singular or plural translation key based on `count`.
 * @param {number} count
 * @param {string} singular
 * @param {string} plural
 * @returns {string}
 */
const singularOrPlural = (count, singular, plural) => (count === 1 ? singular : plural);

/**
 * Maps each validity type key to a function producing { dataForExpiryDate, expiryDateText }.
 */
const VALIDITY_TEXT_MAP = {
  day_number: (count) => ({
    dataForExpiryDate: count,
    expiryDateText: singularOrPlural(count, "trg.validity_field.day", "trg.validity_field.days")
  }),
  year_number: (count) => ({
    dataForExpiryDate: count,
    expiryDateText: singularOrPlural(count, "trg.validity_field.year", "trg.validity_field.years")
  }),
  years_minus_one_day: (count) => ({
    dataForExpiryDate: count,
    expiryDateText: singularOrPlural(
      count,
      "trg.validity_field.year_minus_one_day",
      "trg.validity_field.number_of_years_minus_one_day"
    )
  }),
  current_year: () => ({ dataForExpiryDate: null, expiryDateText: "trg.validity_field.current_year" }),
  next_year: () => ({ dataForExpiryDate: null, expiryDateText: "trg.validity_field.next_year" }),
  december_in_x_years: () => ({
    dataForExpiryDate: "",
    expiryDateText: "trg.validity_field.table_data_december_in_x_years"
  }),
  end_month_in_x_years: () => ({
    dataForExpiryDate: "",
    expiryDateText: "trg.validity_field.table_data_end_month_in_x_years"
  })
};

/**
 * Converts a training's `validity` object into display values for table cells.
 * Returns early with "no expiry" when `validity.no_expiry` is true.
 * For `specific_date`, formats the raw date via the app formatter.
 *
 * @param {object} item - Training record with a `validity` object.
 * @param {Function} format - App date formatter ({ date }) => string.
 * @returns {{ dataForExpiryDate: string|number|null, expiryDateText: string|null }}
 */
export const expiryDateTransformer = (item, format) => {
  if (item.validity?.no_expiry) {
    return { dataForExpiryDate: null, expiryDateText: "trg.validity_field.no_expiry" };
  }

  const result = { dataForExpiryDate: null, expiryDateText: null };

  Object.keys(item.validity)
    .filter((key) => key !== "no_expiry" && item.validity[key])
    .forEach((key) => {
      if (key === "specific_date") {
        result.dataForExpiryDate = format({ date: item.validity[key] });
        return;
      }
      const handler = VALIDITY_TEXT_MAP[key];
      if (handler) {
        Object.assign(result, handler(item.validity[key]));
      }
    });

  return result;
};

/**
 * Maps calendar-relative validity option values to their boolean flag shape for the API.
 */
export const VALIDITY_BOOL_MAP = {
  current_year: { current_year: true },
  next_year: { next_year: true },
  december_in_x_years: { december_in_x_years: true },
  end_month_in_x_years: { end_month_in_x_years: true }
};

/**
 * Extracts the active validity option value from a saved training's `validity` object.
 * Used to pre-select the correct option when editing a training.
 *
 * @param {object} training - Saved training record with a `validity` object.
 * @returns {string|undefined}
 */
export const getInitialValidity = (training) => {
  let result = {};
  forEach(training.validity, (value, key) => {
    if (value) {
      result = validityOptions.find((item) => item.value === key);
    }
  });
  return result?.value;
};
